import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, X } from "react-feather";
import { toast } from "react-toastify";
import {
  createTicketAPI,
  getTicketsAPI,
  updateTicketAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./TicketConsole.module.scss";

const AGENTS = [
  "agent0@meetinghub.ai",
  "agent1@meetinghub.ai",
  "agent2@meetinghub.ai",
];

const STATUS_OPTIONS = ["open", "pending_customer", "in_progress", "resolved"];

// ─── Severity badge ───────────────────────────────────────────────────────────
function SeverityBadge({ value }) {
  const cls =
    {
      critical: styles.sevCritical,
      high: styles.sevHigh,
      medium: styles.sevMedium,
      low: styles.sevLow,
    }[value] || styles.sevLow;
  return <span className={`${styles.sev} ${cls}`}>{value}</span>;
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const cls =
    {
      open: styles.statusOpen,
      in_progress: styles.statusInProgress,
      pending_customer: styles.statusPending,
      resolved: styles.statusResolved,
    }[value] || styles.statusOpen;
  const label = value?.replace(/_/g, " ") ?? value;
  return <span className={`${styles.status} ${cls}`}>{label}</span>;
}

// ─── Assign dropdown ──────────────────────────────────────────────────────────
function AssignDropdown({ row, onAssign }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className={styles.dropdownWrap} ref={ref}>
      <button
        className={`${styles.btnOutline} ${styles.btnSm}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        Assign <ChevronDown size={11} />
      </button>
      {open && (
        <div className={styles.dropdown}>
          {AGENTS.map((a) => (
            <button
              key={a}
              className={styles.dropdownItem}
              onClick={async (e) => {
                e.stopPropagation();
                setOpen(false);
                try {
                  await onAssign(row.id, a);
                } catch (err) {
                  toast.error(err.message);
                }
              }}
            >
              {a}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────
function TicketDrawer({ row, onClose, onReload }) {
  const [comment, setComment] = useState("");

  if (!row) return null;

  return (
    <div
      className={styles.drawerOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h2>{row.ticketNumber}</h2>
          <button className={styles.drawerClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          {/* Meta */}
          <div className={styles.drawerSection}>
            <h3>Details</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <SeverityBadge value={row.severity} />
              <StatusBadge value={row.status} />
            </div>
            <p className={styles.drawerDescription}>{row.description}</p>
          </div>

          {/* Status update */}
          <div className={styles.formGroup}>
            <label>Update status</label>
            <select
              value={row.status}
              onChange={async (e) => {
                try {
                  await updateTicketAPI(row.id, { status: e.target.value });
                  toast.success("Status updated");
                  onReload();
                } catch (err) {
                  toast.error(err.message);
                }
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          {/* Comments */}
          <div className={styles.drawerSection}>
            <h3>Comments</h3>
            <ul className={styles.commentList}>
              {(row.comments || []).map((c) => (
                <li key={c.id} className={styles.commentItem}>
                  <strong>{c.author}</strong>
                  <span>{new Date(c.at).toLocaleString()}</span>
                  <p>{c.body}</p>
                </li>
              ))}
            </ul>
            <div className={styles.commentArea}>
              <textarea
                placeholder="Add a comment…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
            <button
              className={styles.btnPrimary}
              style={{ alignSelf: "flex-start" }}
              onClick={() => {
                toast.success("Comment added (simulated)");
                setComment("");
              }}
            >
              Add comment
            </button>
          </div>

          {/* Linked alerts */}
          {(row.linkedAlerts || []).length > 0 && (
            <div className={styles.drawerSection}>
              <h3>Linked alerts</h3>
              <ul className={styles.linkedList}>
                {row.linkedAlerts.map((a) => (
                  <li key={a.id}>{a.title}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Linked jobs */}
          {(row.linkedJobs || []).length > 0 && (
            <div className={styles.drawerSection}>
              <h3>Linked jobs</h3>
              <ul className={styles.linkedList}>
                {row.linkedJobs.map((j) => (
                  <li key={j}>
                    <Link to={platformUrl(`jobs/${j}`)}>{j}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── New ticket modal ─────────────────────────────────────────────────────────
function NewTicketModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    subject: "",
    tenantCode: "MH-0001",
    severity: "medium",
  });
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    setSaving(true);
    try {
      await createTicketAPI(form);
      toast.success("Ticket created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>New ticket</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label>Subject</label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Describe the issue…"
            />
          </div>
          <div className={styles.formGroup}>
            <label>Tenant code</label>
            <input
              value={form.tenantCode}
              onChange={(e) => setForm({ ...form, tenantCode: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Severity</label>
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "Creating…" : "Create ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Escalate confirm modal ───────────────────────────────────────────────────
function EscalateModal({ ticketId, onClose, onDone }) {
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await updateTicketAPI(ticketId, {
        severity: "critical",
        status: "in_progress",
      });
      toast.success("Escalated to critical");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Escalate ticket?</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.confirmIcon}>
            <AlertTriangle size={24} />
          </div>
          <p className={styles.confirmMessage}>
            Severity will be set to <strong>critical</strong> and status changed
            to <strong>in progress</strong>.
          </p>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.btnWarn}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Escalating…" : "Escalate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TicketConsole() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fSev, setFSev] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fTenant, setFTenant] = useState("");
  const [fAssignee, setFAssignee] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [escalateId, setEscalateId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTicketsAPI({
        severity: fSev || undefined,
        status: fStatus || undefined,
        tenantSearch: fTenant || undefined,
        assignee: fAssignee || undefined,
      });
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [fSev, fStatus, fTenant, fAssignee]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAssign(id, agent) {
    await updateTicketAPI(id, { assignee: agent });
    toast.success("Assigned");
    load();
  }

  async function handleResolve(id) {
    try {
      await updateTicketAPI(id, { status: "resolved" });
      toast.success("Resolved");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const slaBreached = (r) =>
    new Date(r.slaDeadline).getTime() < Date.now() && r.status !== "resolved";

  // ─── Error state ────────────────────────────────────────────────────────────
  if (error && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Ticket console"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Support" },
          ]}
        />
        <div className={styles.body}>
          <div className={styles.emptyState}>
            <AlertTriangle size={40} />
            <h3>Could not load tickets</h3>
            <p>{error.message}</p>
            <button className={styles.btnPrimary} onClick={load}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Ticket console"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Support" },
        ]}
        primaryAction={{ label: "New ticket", onClick: () => setNewOpen(true) }}
        secondaryActions={[
          {
            label: "Tenant view",
            onClick: () => navigate(platformUrl("support/tenant-view")),
          },
          {
            label: "Runbooks",
            onClick: () => navigate(platformUrl("support/runbooks")),
          },
        ]}
      />

      <div className={styles.body}>
        {/* ── Filter bar ──────────────────────────────────────────────── */}
        <div className={styles.filterBar}>
          <div className={styles.filterGroup}>
            <label>Severity</label>
            <select value={fSev} onChange={(e) => setFSev(e.target.value)}>
              <option value="">All severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>Status</label>
            <select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>Tenant</label>
            <input
              placeholder="Search tenant…"
              value={fTenant}
              onChange={(e) => setFTenant(e.target.value)}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>Assignee</label>
            <select
              value={fAssignee}
              onChange={(e) => setFAssignee(e.target.value)}
            >
              <option value="">All assignees</option>
              {AGENTS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <button className={styles.btnPrimary} onClick={load}>
            Apply filters
          </button>
        </div>

        {/* ── Ticket table ─────────────────────────────────────────────── */}
        {loading && !rows.length ? (
          <div className={styles.skeleton} style={{ height: 320 }} />
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>
            <AlertTriangle size={40} />
            <h3>No tickets found</h3>
            <p>Try adjusting your filters or create a new ticket.</p>
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Tickets</h2>
              <span className={styles.cardBadge}>{rows.length} total</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Ticket #</th>
                    <th>Tenant</th>
                    <th>Subject</th>
                    <th>Severity</th>
                    <th>SLA deadline</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className={slaBreached(r) ? styles.slaBreached : ""}
                      onClick={() => setDrawer(r)}
                    >
                      <td>
                        <span className={styles.ticketNum}>
                          {r.ticketNumber}
                        </span>
                      </td>
                      <td>{r.companyName}</td>
                      <td>
                        <span className={styles.subjectText}>{r.subject}</span>
                      </td>
                      <td>
                        <SeverityBadge value={r.severity} />
                      </td>
                      <td>{new Date(r.slaDeadline).toLocaleString()}</td>
                      <td>
                        <span className={styles.assigneeText}>
                          {r.assignee || "—"}
                        </span>
                      </td>
                      <td>
                        <StatusBadge value={r.status} />
                      </td>
                      <td>
                        <div
                          className={styles.rowActions}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AssignDropdown row={r} onAssign={handleAssign} />
                          <button
                            className={`${styles.btnWarn} ${styles.btnSm}`}
                            onClick={() => setEscalateId(r.id)}
                          >
                            Escalate
                          </button>
                          <button
                            className={`${styles.btnSuccess} ${styles.btnSm}`}
                            onClick={() => handleResolve(r.id)}
                            disabled={r.status === "resolved"}
                          >
                            Resolve
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Ticket drawer ──────────────────────────────────────────────── */}
      {drawer && (
        <TicketDrawer
          row={drawer}
          onClose={() => setDrawer(null)}
          onReload={load}
        />
      )}

      {/* ── Escalate confirm ───────────────────────────────────────────── */}
      {escalateId && (
        <EscalateModal
          ticketId={escalateId}
          onClose={() => setEscalateId(null)}
          onDone={load}
        />
      )}

      {/* ── New ticket modal ───────────────────────────────────────────── */}
      {newOpen && (
        <NewTicketModal onClose={() => setNewOpen(false)} onCreated={load} />
      )}
    </div>
  );
}
